import React, {useContext} from 'react'
import AppContext from '../appContext'
import LimeProgramLabInputRow from '../controllers/LimeProgramLabInputRow'
import NullableMeasureValue from '../controllers/NullableMeasureValue'
import MeasurementUnit from '../controllers/MeasurementUnit'
import {getValueNullable} from '../pages/program/Helpers'
import {customUnits, unitCustom} from '../pages/program/CustomUnits'
import {formatValue, formatMeasureElement} from '../pages/liming/limeSoilUtils'

export interface SampleDataColumn {
    header: React.ReactNode
    row: (item: LimeProgramLabInputRow) => React.ReactNode
}

interface SampleDataOverlayProps {
    row: LimeProgramLabInputRow
    meq100gMode: boolean
    system: MeasurementUnit
    extraColumns?: SampleDataColumn[]
}

const SampleDataOverlay: React.FC<SampleDataOverlayProps> = ({row, meq100gMode, system, extraColumns}) => {
    const app = useContext(AppContext)
    const unitLabel = meq100gMode ? 'meq/100g' : unitCustom(system, customUnits.mgKg_ppm)
    const th = "px-2 py-1 text-left"
    const td = "px-2 py-1"

    function formatMeasure(value: NullableMeasureValue): string {
        const v = getValueNullable(system, value)
        if (v === null) return '-'
        return v.toFixed(2)
    }

    return (
        <div className="absolute top-0 left-0 right-0 overflow-x-auto bg-white/90 border-b border-gray-300 shadow-sm z-10">
            <table className="w-full text-xs">
                <thead>
                    <tr className="bg-gray-200/90">
                        <th className={th}>{app.word('reference_nr')}</th>
                        <th className={th}>{app.word('sample_depth')}</th>
                        <th className={th}>pH(H2O)</th>
                        <th className={th}>P Bray I</th>
                        <th className={th}>K ({unitLabel})</th>
                        <th className={th}>Na ({unitLabel})</th>
                        <th className={th}>Ca ({unitLabel})</th>
                        <th className={th}>Mg ({unitLabel})</th>
                        <th className={th}>Exch H</th>
                        <th className={th}>%Ca</th>
                        <th className={th}>%Mg</th>
                        <th className={th}>%K</th>
                        <th className={th}>%Na</th>
                        <th className={th}>{app.word('acid_saturated')}</th>
                        <th className={th}>Ca:Mg</th>
                        <th className={th}>(Ca+Mg)/K</th>
                        <th className={th}>Mg:K</th>
                        <th className={th}>S-Value</th>
                        <th className={th}>Na:K</th>
                        <th className={th}>CEC</th>
                        <th className={th}>{app.word('density')}</th>
                        {extraColumns?.map((c, i) => <th key={i} className={th}>{c.header}</th>)}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className={td}>{row.referenceNr || row.landRef}</td>
                        <td className={td}>{app.word(row.sampleDepth)}</td>
                        <td className={td}>{formatValue(row.phH2O)}</td>
                        <td className={td}>{formatMeasure(row.pBrayI)}</td>
                        <td className={td}>{formatMeasureElement(system, row.k, 'K', meq100gMode)}</td>
                        <td className={td}>{formatMeasureElement(system, row.na, 'Na', meq100gMode)}</td>
                        <td className={td}>{formatMeasureElement(system, row.ca, 'Ca', meq100gMode)}</td>
                        <td className={td}>{formatMeasureElement(system, row.mg, 'Mg', meq100gMode)}</td>
                        <td className={td}>{formatValue(row.exchH)}</td>
                        <td className={td}>{formatValue(row.caPercent)}</td>
                        <td className={td}>{formatValue(row.mgPercent)}</td>
                        <td className={td}>{formatValue(row.kPercent)}</td>
                        <td className={td}>{formatValue(row.naPercent)}</td>
                        <td className={td}>{formatValue(row.acidSat)}</td>
                        <td className={td}>{formatValue(row.caMgRatio)}</td>
                        <td className={td}>{formatValue(row.caMgKRatio)}</td>
                        <td className={td}>{formatValue(row.mgKRatio)}</td>
                        <td className={td}>{formatValue(row.sValue)}</td>
                        <td className={td}>{formatValue(row.naKRatio)}</td>
                        <td className={td}>{formatValue(row.cec)}</td>
                        <td className={td}>{formatValue(row.density)}</td>
                        {extraColumns?.map((c, i) => <td key={i} className={td}>{c.row(row)}</td>)}
                    </tr>
                </tbody>
            </table>
        </div>
    )
}

export default SampleDataOverlay
